/* eslint-disable react/jsx-props-no-spreading */
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import CloseIcon from "@mui/icons-material/Close"
import PhotoIcon from "@mui/icons-material/Photo"
import {
    CardMedia,
    CircularProgress,
    Divider,
    IconButton,
    useTheme,
} from "@mui/material"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { ReactElement } from "react"

import {
    TT_ATTCH_UPLOADING,
    TT_ATTCH_UPLOAD_SUCCESS,
} from "../../constants/tooltips"
import { useScreenSize } from "../../hooks/useScreenSize"
import { IUpload } from "../../services/middleware/types/singleApplication/uploadAttachmentTypes"
import { CARD_RADIUS_MULTIPLIER } from "../../themes/defaultThemeParams"
import { bytesToMb } from "../../utils/general/generalUtils"
import AppButton from "../buttons/appButton"
import AppTooltip from "../helper/appTooltip"
import DisplayIf from "../wrappers/displayIf"
import DisplayIfElse from "../wrappers/displayIfElse"

const ICON_SIZE = 40

interface UploadedAttachmentProps {
    upload: IUpload
    deleteUpload: (nameToRemove: string) => void
}

/**
 * Displays a single attachment once it has been uploaded into the UI and API
 * successfully.
 */
const UploadedAttachment = ({
    upload,
    deleteUpload,
}: UploadedAttachmentProps): ReactElement => {
    const theme = useTheme()
    const { isTabletOrMob } = useScreenSize()
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            borderRadius={CARD_RADIUS_MULTIPLIER}
            paddingX={3}
            paddingY={1}
            sx={{
                backgroundColor:
                    theme.palette.mode === "dark"
                        ? theme.palette.background.contentBackground
                        : theme.palette.primary.light,
            }}
        >
            <Stack direction="row" alignItems="center">
                <DisplayIfElse
                    condition={upload.loading}
                    displayIfTrue={
                        <AppTooltip title={TT_ATTCH_UPLOADING}>
                            <CircularProgress size={ICON_SIZE} />
                        </AppTooltip>
                    }
                    displayIfFalse={
                        <AppTooltip title={TT_ATTCH_UPLOAD_SUCCESS}>
                            <CheckCircleOutlineIcon
                                sx={{ fontSize: ICON_SIZE }}
                            />
                        </AppTooltip>
                    }
                />
                <Stack
                    direction="row"
                    spacing={10}
                    alignItems="center"
                    marginLeft={isTabletOrMob ? 3 : 6}
                >
                    <DisplayIf condition={!isTabletOrMob}>
                        {upload?.preview ? (
                            <CardMedia
                                component="img"
                                sx={{
                                    borderRadius: "10px",
                                    width: "70px",
                                    height: "70px",
                                }}
                                src={upload?.preview}
                                width="80px"
                            />
                        ) : (
                            <PhotoIcon sx={{ fontSize: 60 }} />
                        )}
                    </DisplayIf>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        divider={<Divider orientation="vertical" flexItem />}
                    >
                        <Typography variant="h6">{upload.name}</Typography>
                        {!isTabletOrMob && (
                            <Typography variant="h6" fontWeight="light">
                                {upload?.size
                                    ? `${bytesToMb(upload.size).toFixed(2)} MB`
                                    : ""}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <DisplayIfElse
                condition={isTabletOrMob}
                displayIfTrue={
                    <IconButton
                        color="error"
                        onClick={() => deleteUpload(upload.name)}
                    >
                        <CloseIcon />
                    </IconButton>
                }
                displayIfFalse={
                    <AppButton
                        onClick={() => deleteUpload(upload.name)}
                        variant="contained"
                        color="error"
                        endIcon={<CloseIcon />}
                    >
                        Remove
                    </AppButton>
                }
            />
        </Stack>
    )
}

export default UploadedAttachment
